import { useState, useEffect, useRef, useCallback, RefObject } from 'react'

// ---------------------------------------------------------------------------
// State machine: idle → pulling → triggered → refreshing → completing → idle
// ---------------------------------------------------------------------------
export type PullToRefreshState = 'idle' | 'pulling' | 'triggered' | 'refreshing' | 'completing'

interface UsePullToRefreshOptions {
  onRefresh: () => void | Promise<void>
  /** Distance (px) the user must drag before a release triggers refresh */
  threshold?: number
  disabled?: boolean
}

export interface UsePullToRefreshResult {
  /** Current phase of the pull-to-refresh lifecycle */
  state: PullToRefreshState
  /** Damped pull distance in px (0 when idle/refreshing/completing) */
  pullDistance: number
  /** Convenience flag: true while the refresh callback is running */
  isRefreshing: boolean
}

// Exponential approach damping — feels like a spring resisting the pull.
// MAX caps the visual displacement; k controls how fast resistance builds.
const MAX_PULL = 128
const DAMPING_K = 0.4

function damp(raw: number): number {
  return MAX_PULL * (1 - Math.exp((-DAMPING_K * raw) / MAX_PULL))
}

/**
 * Detects a downward touch-pull gesture at the top of a scroll container and
 * triggers a refresh callback when the drag exceeds the threshold.
 *
 * Returns the current state and damped pull distance so the caller (or the
 * shared `PullToRefreshIndicator` component) can render a visual indicator.
 */
export function usePullToRefresh(
  ref: RefObject<HTMLElement | null>,
  { onRefresh, threshold = 72, disabled = false }: UsePullToRefreshOptions,
): UsePullToRefreshResult {
  const [state, setState] = useState<PullToRefreshState>('idle')
  const [pullDistance, setPullDistance] = useState(0)

  const startYRef = useRef<number | null>(null)
  const isPullingRef = useRef(false)
  const rawDistRef = useRef(0)
  const onRefreshRef = useRef(onRefresh)

  useEffect(() => { onRefreshRef.current = onRefresh }, [onRefresh])

  const runRefresh = useCallback(async () => {
    setState('refreshing')
    setPullDistance(0)
    try {
      await onRefreshRef.current()
    } finally {
      setState('completing')
      // Brief pause so the user sees the "done" state before it disappears
      setTimeout(() => setState('idle'), 300)
    }
  }, [])

  useEffect(() => {
    const el = ref.current
    if (!el || disabled) return

    const onTouchStart = (e: TouchEvent) => {
      if (el.scrollTop > 0 || state === 'refreshing' || state === 'completing') return
      startYRef.current = e.touches[0].clientY
      isPullingRef.current = true
      rawDistRef.current = 0
    }

    const onTouchMove = (e: TouchEvent) => {
      if (!isPullingRef.current || startYRef.current === null) return
      const raw = e.touches[0].clientY - startYRef.current
      if (raw <= 0) {
        rawDistRef.current = 0
        setPullDistance(0)
        setState('idle')
        return
      }
      // Prevent scroll while pulling down from top
      if (el.scrollTop === 0) e.preventDefault()

      rawDistRef.current = raw
      const damped = damp(raw)
      setPullDistance(damped)
      setState(damped >= threshold ? 'triggered' : 'pulling')
    }

    const onTouchEnd = () => {
      if (!isPullingRef.current) return
      isPullingRef.current = false
      const damped = damp(rawDistRef.current)
      rawDistRef.current = 0
      startYRef.current = null

      if (damped >= threshold) {
        // Transition to refreshing — indicator stays visible
        runRefresh()
      } else {
        setPullDistance(0)
        setState('idle')
      }
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd)
    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
    }
  }, [ref, threshold, disabled, state, runRefresh])

  return {
    state,
    pullDistance,
    isRefreshing: state === 'refreshing' || state === 'completing',
  }
}
